import { z } from "zod";
import { LIBRARY_UNITS, type LibraryUnit } from "./library.js";

/**
 * Conditiemeting volgens NEN 2767 (docs/CONDITIEMETING.md). Begrippen: een asset is een bouwdeel;
 * componenten en onderdelen bestaan alleen als keuze en tekst bij een gebrek.
 *
 * De rekenregels komen uit NEN 2767-1+C1:2019 (hoofdstuk 5, bijlage B en F). De gebrekenlijsten van
 * NEN 2767-2 zitten niet in de code: een organisatie met licentie importeert ze als JSON (§6.3).
 */

// ── Assets: soort ──

/** Meter: docs/METERSTANDEN.md. Arbeidsmiddel: ladder, trap, elektrisch gereedschap (docs/ARBEIDSMIDDELEN.md); geen plek op de tekening nodig. Beide doen niet mee in een conditiemeting. */
export const ASSET_KINDS = ["installation", "building_part", "equipment", "meter"] as const;
export type AssetKind = (typeof ASSET_KINDS)[number];
export const ASSET_KIND_LABELS: Record<AssetKind, string> = {
  installation: "Installatie",
  building_part: "Bouwkundig",
  equipment: "Arbeidsmiddel",
  meter: "Meter",
};
/** Meervoud voor het filter in de assetlijst. */
export const ASSET_KIND_FILTER_LABELS: Record<AssetKind, string> = {
  installation: "Installaties",
  building_part: "Bouwkundig",
  equipment: "Arbeidsmiddelen",
  meter: "Meters",
};

// ── Meting ──

export const ASSESSMENT_STATUSES = ["draft", "in_progress", "completed", "cancelled"] as const;
export type AssessmentStatus = (typeof ASSESSMENT_STATUSES)[number];
export const ASSESSMENT_STATUS_LABELS: Record<AssessmentStatus, string> = {
  draft: "Instructie opstellen",
  in_progress: "In uitvoering",
  completed: "Afgerond",
  cancelled: "Geannuleerd",
};
/** Na afronden of annuleren ligt een meting vast (besluit 6). */
export const ASSESSMENT_LOCKED: ReadonlySet<AssessmentStatus> = new Set(["completed", "cancelled"]);

export const DISCIPLINES = ["B", "W", "E", "T"] as const;
export type Discipline = (typeof DISCIPLINES)[number];
export const DISCIPLINE_LABELS: Record<Discipline, string> = {
  B: "Bouwkundig",
  W: "Werktuigbouwkundig",
  E: "Elektrotechnisch",
  T: "Transport (liften e.d.)",
};

export const DEFECT_SEVERITIES = ["minor", "serious", "critical"] as const;
export type DefectSeverity = (typeof DEFECT_SEVERITIES)[number];
export const DEFECT_SEVERITY_LABELS: Record<DefectSeverity, string> = {
  minor: "Gering",
  serious: "Serieus",
  critical: "Ernstig",
};
export const DEFECT_INTENSITIES = ["initial", "advanced", "final"] as const;
export type DefectIntensity = (typeof DEFECT_INTENSITIES)[number];
export const DEFECT_INTENSITY_LABELS: Record<DefectIntensity, string> = {
  initial: "Beginstadium",
  advanced: "Gevorderd stadium",
  final: "Eindstadium",
};
export const DEFECT_LOCATIONS = ["asset", "component", "subcomponent"] as const;
export type DefectLocation = (typeof DEFECT_LOCATIONS)[number];
export const DEFECT_LOCATION_LABELS: Record<DefectLocation, string> = {
  asset: "Asset zelf",
  component: "Component",
  subcomponent: "Onderdeel van een component",
};

// ── Gebrekenlijst ──

/**
 * Soort gebrek in de lijst. `own` (eigen gebrek: ernst door de inspecteur, toelichting verplicht)
 * staat alleen in de systeemlijst (§6.1).
 */
export const DEFECT_TYPES = ["specific", "component_ageing", "subcomponent_ageing", "optional", "own"] as const;
export type DefectType = (typeof DEFECT_TYPES)[number];
export const DEFECT_TYPE_LABELS: Record<DefectType, string> = {
  specific: "Specifiek gebrek",
  component_ageing: "Veroudering component",
  subcomponent_ageing: "Veroudering onderdeel van component",
  optional: "Facultatief gebrek",
  own: "Eigen gebrek",
};
export const CATALOG_SOURCES = ["system", "nen2767-2", "own"] as const;
export type CatalogSource = (typeof CATALOG_SOURCES)[number];
export const CATALOG_SOURCE_LABELS: Record<CatalogSource, string> = {
  system: "Generieke gebreken (Klantpunt)",
  "nen2767-2": "NEN 2767-2",
  own: "Eigen lijst",
};
export const CODE_SCHEMES = ["generic", "nen2767-2008-nlsfb", "nlbe-sfb-2025"] as const;
export type CodeScheme = (typeof CODE_SCHEMES)[number];
export const CODE_SCHEME_LABELS: Record<CodeScheme, string> = {
  generic: "Geen bouwdeelcodes (generiek)",
  "nen2767-2008-nlsfb": "NEN 2767-2 (NL-SfB)",
  "nlbe-sfb-2025": "NLBE-SfB 2025",
};

/**
 * Soorten gebreken uit het raamwerk van NEN 2767-1 met hun vaste ernst. Een eigen gebrek krijgt een
 * soort; de ernst volgt daaruit. De codes zijn die uit de gebrekcodes van NEN 2767-2 (bijv. EW = ernstig,
 * werking primair).
 */
export const DEFECT_CATEGORIES: readonly { code: string; name: string; severity: DefectSeverity }[] = [
  { code: "EW", name: "Werking primair", severity: "critical" },
  { code: "EC", name: "Constructief primair", severity: "critical" },
  { code: "EM", name: "Materiaalintrinsiek", severity: "critical" },
  { code: "EB", name: "Basiskwaliteit", severity: "critical" },
  { code: "SW", name: "Werking secundair", severity: "serious" },
  { code: "SC", name: "Constructief secundair", severity: "serious" },
  { code: "SM", name: "Materiaaloppervlak", severity: "serious" },
  { code: "SB", name: "Basiskwaliteit en veroudering componenten", severity: "serious" },
  { code: "GA", name: "Afwerking", severity: "minor" },
  { code: "GO", name: "Onderhoud", severity: "minor" },
  { code: "GB", name: "Basiskwaliteit en veroudering onderdelen van componenten", severity: "minor" },
  { code: "GV", name: "Verval", severity: "minor" },
];
export const categorySeverity = (name: string | null | undefined): DefectSeverity | null =>
  DEFECT_CATEGORIES.find((c) => c.name.toLowerCase() === name?.trim().toLowerCase())?.severity ?? null;

/** Aspecten voor facultatieve gebreken (§1: alleen vastleggen, niet uitwerken). */
export const OPTIONAL_ASPECTS = ["Brandveiligheid", "Energie", "Asbest", "Overig"] as const;

/** Vaste ids van de systeemlijst, zodat server, migratie en tests dezelfde rijen bedoelen. */
export const SYSTEM_CATALOG_ID = "00000000-0000-4000-8c00-000000000001";
export interface GenericDefect {
  id: string;
  defectCode: string;
  description: string;
  defectType: DefectType;
  defectCategory: string | null;
  severity: DefectSeverity | null;
  optionalAspect: string | null;
}
/**
 * Generieke gebreken (§6.1): volgen uit de methodiek, niet uit de beschermde tabellen van
 * NEN 2767-2. Gelden voor elke asset.
 */
export const GENERIC_DEFECTS: readonly GenericDefect[] = [
  {
    id: "00000000-0000-4000-8c01-000000000001",
    defectCode: "G-VC",
    description: "Veroudering component",
    defectType: "component_ageing",
    defectCategory: "Basiskwaliteit en veroudering componenten",
    severity: "serious",
    optionalAspect: null,
  },
  {
    id: "00000000-0000-4000-8c01-000000000002",
    defectCode: "G-VO",
    description: "Veroudering onderdeel van component",
    defectType: "subcomponent_ageing",
    defectCategory: "Basiskwaliteit en veroudering onderdelen van componenten",
    severity: "minor",
    optionalAspect: null,
  },
  {
    id: "00000000-0000-4000-8c01-000000000003",
    defectCode: "G-EIGEN",
    description: "Eigen gebrek",
    defectType: "own",
    defectCategory: null,
    severity: null,
    optionalAspect: null,
  },
  {
    id: "00000000-0000-4000-8c01-000000000004",
    defectCode: "G-VERVAL",
    description: "Verval (niet waarneembaar, op leeftijd)",
    defectType: "specific",
    defectCategory: "Verval",
    severity: "minor",
    optionalAspect: null,
  },
  ...OPTIONAL_ASPECTS.map((aspect, k) => ({
    id: `00000000-0000-4000-8c01-00000000001${k}`,
    defectCode: `G-FAC-${k + 1}`,
    description: `Facultatief gebrek: ${aspect.toLowerCase()}`,
    defectType: "optional" as const,
    defectCategory: null,
    severity: null,
    optionalAspect: aspect,
  })),
];

// ── Regels bij een gebrek (§4) ──

/** Wat de gebrekenlijst over een gebrek vastlegt; leeg bij een eigen gebrek zonder lijstregel. */
export interface CatalogDefectRule {
  defectType: DefectType;
  severity: DefectSeverity | null;
  requiredIntensity: DefectIntensity | null;
  /** Regel uit een lijst met een eigen naam (geen generiek facultatief gebrek): geen toelichting nodig. */
  named?: boolean;
}
export interface DefectInput {
  defectLocation: DefectLocation;
  componentNote?: string | null;
  description?: string | null;
  severity?: DefectSeverity | null;
  intensity?: DefectIntensity | null;
  extentPercent?: number | null;
  /** Soort bij een eigen gebrek (raamwerk NEN 2767-1); bepaalt de ernst. */
  defectCategory?: string | null;
}
export type DefectCheck =
  | { ok: true; severity: DefectSeverity; intensity: DefectIntensity | null; isOptional: boolean }
  | {
      ok: false;
      field: "componentNote" | "description" | "severity" | "intensity" | "defectLocation" | "extentPercent";
      message: string;
    };

/** Itemregel uit de database naar de regel voor `checkDefect`. */
export const defectRule = (i: {
  defectType: DefectType;
  severity: DefectSeverity | null;
  requiredIntensity: DefectIntensity | null;
  partCode?: string | null;
}): CatalogDefectRule => ({
  defectType: i.defectType,
  severity: i.severity,
  requiredIntensity: i.requiredIntensity,
  named: Boolean(i.partCode),
});

/**
 * Regels §4.1, 4.2, 4.4 en 4.6: omschrijving bij component en onderdeel, vaste ernst bij veroudering
 * en bij een gebrek uit de lijst, verplichte intensiteit, en bij een eigen gebrek een toelichting en
 * een zelfgekozen ernst. Geeft de ernst en intensiteit die worden opgeslagen.
 */
export function checkDefect(input: DefectInput, rule: CatalogDefectRule | null): DefectCheck {
  const note = input.componentNote?.trim();
  if (input.defectLocation !== "asset" && !note)
    return {
      ok: false,
      field: "componentNote",
      message:
        input.defectLocation === "component"
          ? "Omschrijf de component, bijv. brander"
          : "Omschrijf het onderdeel, bijv. kitwerk",
    };
  const type = rule?.defectType ?? "own";
  if (type === "component_ageing" && input.defectLocation !== "component")
    return { ok: false, field: "defectLocation", message: "Veroudering van een component hoort bij een component" };
  if (type === "subcomponent_ageing" && input.defectLocation !== "subcomponent")
    return {
      ok: false,
      field: "defectLocation",
      message: "Veroudering van een onderdeel hoort bij een onderdeel van een component",
    };
  // Vaste ernst: veroudering (§4.2), elk gebrek uit de lijst met ernst (§4.3) en een eigen gebrek met een soort.
  const fixed: DefectSeverity | null =
    type === "component_ageing"
      ? "serious"
      : type === "subcomponent_ageing"
        ? "minor"
        : (rule?.severity ?? (type === "own" ? categorySeverity(input.defectCategory) : null));
  if (fixed && input.severity && input.severity !== fixed)
    return { ok: false, field: "severity", message: `De ernst ligt vast op ${DEFECT_SEVERITY_LABELS[fixed]}` };
  const isOptional = type === "optional";
  // Facultatief: telt niet mee in de score, de ernst is dan alleen informatief.
  const severity = fixed ?? input.severity ?? (isOptional ? "minor" : null);
  if (!severity) return { ok: false, field: "severity", message: "Kies de soort of de ernst" };
  if ((type === "own" || (isOptional && !rule?.named)) && !input.description?.trim())
    return { ok: false, field: "description", message: "Licht het gebrek toe" };
  const required = rule?.requiredIntensity ?? null;
  if (required && input.intensity && input.intensity !== required)
    return {
      ok: false,
      field: "intensity",
      message: `De intensiteit ligt vast op ${DEFECT_INTENSITY_LABELS[required]}`,
    };
  const intensity = required ?? input.intensity ?? null;
  // Score volgens tabel 5 vraagt intensiteit en omvang (§5.2).
  if (!isOptional && !intensity) return { ok: false, field: "intensity", message: "Kies de intensiteit" };
  if (!isOptional && (input.extentPercent == null || !(input.extentPercent > 0)))
    return { ok: false, field: "extentPercent", message: "Vul de omvang in (meer dan 0%)" };
  return { ok: true, severity, intensity, isOptional };
}

// ── Rekenregels NEN 2767-1+C1:2019 (hoofdstuk 5, bijlage B, bijlage F) ──

export type ExtentClass = 1 | 2 | 3 | 4 | 5;
/** Omvangklassen (tabel 4): < 2 %, 2 tot 10 %, 10 tot 30 %, 30 tot 70 %, 70 % en meer. */
export const EXTENT_CLASS_LABELS: Record<ExtentClass, string> = {
  1: "minder dan 2%",
  2: "2 tot 10%",
  3: "10 tot 30%",
  4: "30 tot 70%",
  5: "70% of meer",
};
/** Snelkeuze per omvangklasse in het veld: een waarde midden in de klasse. */
export const EXTENT_CLASS_PICKS: Record<ExtentClass, number> = { 1: 1, 2: 5, 3: 20, 4: 50, 5: 100 };

export function extentClass(extentPercent: number): ExtentClass {
  const p = extentPercent;
  return p < 2 ? 1 : p < 10 ? 2 : p < 30 ? 3 : p < 70 ? 4 : 5;
}

/** Tabel 5: conditie per ernst, intensiteit en omvangklasse. */
const TABLE_5: Record<DefectSeverity, readonly (readonly number[])[]> = {
  minor: [
    [1, 1, 1, 1, 2],
    [1, 1, 1, 2, 3],
    [1, 1, 2, 3, 4],
  ],
  serious: [
    [1, 1, 1, 2, 3],
    [1, 1, 2, 3, 4],
    [1, 2, 3, 4, 5],
  ],
  critical: [
    [1, 1, 2, 3, 4],
    [1, 2, 3, 4, 5],
    [2, 3, 4, 5, 6],
  ],
};
const INTENSITY_INDEX: Record<DefectIntensity, number> = { initial: 0, advanced: 1, final: 2 };

export function defectScore(severity: DefectSeverity, intensity: DefectIntensity, cls: ExtentClass): ConditionScore {
  return TABLE_5[severity][INTENSITY_INDEX[intensity]]![cls - 1] as ConditionScore;
}

/** Tabel B.1: correctiefactor per conditie. */
export const CORRECTION_FACTORS: Record<ConditionScore, number> = { 1: 1, 2: 1.02, 3: 1.1, 4: 1.3, 5: 1.7, 6: 2 };

/** Tabel B.2: conditie uit de uitkomst (gecorrigeerde omvang of waarde gedeeld door het totaal). */
export function scoreFromOutcome(outcome: number): ConditionScore {
  const x = Math.round(outcome * 10000) / 10000;
  return x <= 1.01 ? 1 : x <= 1.04 ? 2 : x <= 1.15 ? 3 : x <= 1.4 ? 4 : x <= 1.78 ? 5 : 6;
}

/** Welke methode leidend is bij één gebrek (of één groep): de norm laat dit vooraf kiezen. */
export const SCORE_METHODS = ["table5", "annexB"] as const;
export type ScoreMethod = (typeof SCORE_METHODS)[number];
export const SCORE_METHOD_LABELS: Record<ScoreMethod, string> = {
  table5: "Tabel 5 (conditie per gebrek)",
  annexB: "Bijlage B (correctiefactoren)",
};

export interface ScoredDefect {
  id: string;
  severity: DefectSeverity;
  intensity: DefectIntensity | null;
  extentPercent: number | null;
  /** Gestapeld: zit op dezelfde plek als dit gebrek (§5.3, situatie 1). */
  stackedOnId?: string | null;
  isOptional?: boolean;
}
export type AssetScoreHow = "none" | "table5" | "summed" | "stacked" | "annexB";
export const ASSET_SCORE_HOW_LABELS: Record<AssetScoreHow, string> = {
  none: "geen gebreken",
  table5: "tabel 5",
  summed: "tabel 5, omvang opgeteld",
  stacked: "tabel 5, gestapelde gebreken",
  annexB: "bijlage B",
};
export interface AssetScoreRow {
  severity: DefectSeverity;
  intensity: DefectIntensity;
  extentPercent: number;
  /** Conditie bij deze omvang (tabel 5) of bij 100 % (bijlage B). */
  score: ConditionScore;
  factor: number;
}
export interface AssetScoreResult {
  score: ConditionScore;
  /** Uitkomst volgens bijlage B; telt bij het samenvoegen als correctiefactor. */
  outcome: number;
  how: AssetScoreHow;
  rows: AssetScoreRow[];
  /** Gebreken die niet meetellen omdat ze gestapeld zijn op een erger gebrek. */
  ignored: string[];
}

/**
 * Conditie van één asset uit de niet-facultatieve gebreken (§5.2, §5.3, bijlage B).
 * Gestapelde gebreken: per plek telt het gebrek met de slechtste conditie bij 100 %. Gebreken met
 * dezelfde ernst en intensiteit: omvang optellen. Eén groep: tabel 5 (of bijlage B als die vooraf
 * leidend is gekozen). Meer groepen: bijlage B. Zonder intensiteit telt beginstadium, zonder omvang
 * 100 % (oude metingen; nieuwe gebreken hebben beide verplicht).
 */
export function assetScore(defects: readonly ScoredDefect[], method: ScoreMethod = "table5"): AssetScoreResult {
  const ds = defects
    .filter((d) => !d.isOptional && (d.extentPercent ?? 100) > 0)
    .map((d) => ({ ...d, intensity: d.intensity ?? "initial", pct: Math.min(100, d.extentPercent ?? 100) }));
  if (!ds.length) return { score: 1, outcome: 1, how: "none", rows: [], ignored: [] };
  const at100 = (d: { severity: DefectSeverity; intensity: DefectIntensity }) =>
    defectScore(d.severity, d.intensity, 5);
  type D = (typeof ds)[number];
  const spots: D[][] = [];
  for (const d of ds) {
    const spot = d.stackedOnId ? spots.find((s) => s.some((x) => x.id === d.stackedOnId)) : undefined;
    if (spot) spot.push(d);
    else spots.push([d]);
  }
  const ignored: string[] = [];
  const leads = spots.map((s) => {
    const lead = s.reduce((a, b) => (at100(b) > at100(a) ? b : a));
    ignored.push(...s.filter((x) => x !== lead).map((x) => x.id));
    return lead;
  });
  const groups: { severity: DefectSeverity; intensity: DefectIntensity; pct: number; n: number }[] = [];
  for (const l of leads) {
    const g = groups.find((x) => x.severity === l.severity && x.intensity === l.intensity);
    if (g) {
      g.pct += l.pct;
      g.n++;
    } else groups.push({ severity: l.severity, intensity: l.intensity, pct: l.pct, n: 1 });
  }
  if (groups.length === 1 && method === "table5") {
    const g = groups[0]!;
    const pct = Math.min(100, g.pct);
    const score = defectScore(g.severity, g.intensity, extentClass(pct));
    return {
      score,
      outcome: CORRECTION_FACTORS[score],
      how: ignored.length ? "stacked" : g.n > 1 ? "summed" : "table5",
      rows: [
        { severity: g.severity, intensity: g.intensity, extentPercent: pct, score, factor: CORRECTION_FACTORS[score] },
      ],
      ignored,
    };
  }
  let used = 0;
  let corrected = 0;
  const rows: AssetScoreRow[] = groups.map((g) => {
    const score = at100(g);
    const factor = CORRECTION_FACTORS[score];
    used += g.pct;
    corrected += g.pct * factor;
    return { severity: g.severity, intensity: g.intensity, extentPercent: g.pct, score, factor };
  });
  corrected += Math.max(0, 100 - used);
  const outcome = corrected / Math.max(100, used);
  return { score: scoreFromOutcome(outcome), outcome, how: "annexB", rows, ignored };
}

export interface AggregateItem {
  /** Vervangingswaarde (weging). */
  value: number;
  score: number;
  /** Berekende uitkomst van de asset (bijlage B); anders de factor uit tabel B.1. */
  outcome?: number | null;
}
/** Samenvoegen tot een samenstel of gebouw (§5.5, bijlage B): gewogen correctiefactoren. */
export function aggregateScore(
  items: readonly AggregateItem[],
): { score: ConditionScore; outcome: number; value: number } | null {
  const xs = items.filter((i) => i.value > 0 && i.score >= 1 && i.score <= 6);
  const value = xs.reduce((a, i) => a + i.value, 0);
  if (!value) return null;
  const corrected = xs.reduce((a, i) => a + i.value * (i.outcome ?? CORRECTION_FACTORS[i.score as ConditionScore]), 0);
  const outcome = corrected / value;
  return { score: scoreFromOutcome(outcome), outcome, value };
}

/**
 * Vangnet en verval (§5.4, bijlage F): ouder dan 50 % van de levensduur → beginstadium, ouder dan
 * 75 % → gevorderd, ouder dan 87,5 % → eindstadium. Verval is een gering gebrek met een omvang
 * (het deel dat niet door waarneming te beoordelen is); de theoretische conditie hieronder is die bij
 * 100 % omvang (2, 3 of 4).
 */
export const SAFETY_NET_THRESHOLDS: readonly { above: number; intensity: DefectIntensity; score: 2 | 3 | 4 }[] = [
  { above: 0.875, intensity: "final", score: 4 },
  { above: 0.75, intensity: "advanced", score: 3 },
  { above: 0.5, intensity: "initial", score: 2 },
];

/** (jaar − bouwjaar) / levensduur op de datum; null zonder bouwjaar of levensduur. */
export function ageRatio(
  installedYear: number | null | undefined,
  lifespanYears: number | null | undefined,
  onDate: string,
): number | null {
  if (!installedYear || !lifespanYears || lifespanYears <= 0) return null;
  return Math.max(0, Number(onDate.slice(0, 4)) - installedYear) / lifespanYears;
}

const ratioPct = (ratio: number) => Math.round(ratio * 10000) / 100; // drijvende komma: 0,75 blijft 75 %

/** Theoretische conditie (verval bij 100 % omvang): 1 tot en met 4. */
export function theoreticalScore(ratio: number | null): 1 | 2 | 3 | 4 | null {
  if (ratio == null) return null;
  return SAFETY_NET_THRESHOLDS.find((t) => ratioPct(ratio) > t.above * 100)?.score ?? 1;
}

/** Vangnet in de inspectie-instructie: uit, of verval tot en met conditie 2, 3 of 4. */
export const SAFETY_NET_MAX = [2, 3, 4] as const;
export type SafetyNetMax = (typeof SAFETY_NET_MAX)[number];

/** Intensiteit van het verval op leeftijd, begrensd door de instructie; null = geen verval. */
export function decayIntensity(ratio: number | null, max: SafetyNetMax | null): DefectIntensity | null {
  if (ratio == null || max == null) return null;
  const t = SAFETY_NET_THRESHOLDS.find((x) => ratioPct(ratio) > x.above * 100);
  if (!t) return null;
  const capped = SAFETY_NET_THRESHOLDS.find((x) => x.score === Math.min(t.score, max))!;
  return capped.intensity;
}

/** Soort gebrek "Verval" (bijlage F), in de generieke lijst en in de NEN-lijsten. */
export const DECAY_CATEGORY = "Verval";

// ── API ──

const optText = (n: number) =>
  z.preprocess((v) => (typeof v === "string" && v.trim() === "" ? null : v), z.string().trim().max(n).nullish());

/** Inspectie-instructie (§5.1, §10.1). Vrije tekst per onderwerp; de vangnet standaard uit. */
export const assessmentInstructionSchema = z.object({
  /** Wat wel en niet wordt beoordeeld. */
  scope: optText(4000),
  /** Assets die buiten deze meting vallen. */
  excludedAssetIds: z.array(z.uuid()).max(5000).default([]),
  /** Welke delen een eigen asset zijn en wat als component bij een asset hoort (§4.5). */
  ownAssets: optText(4000),
  detailLevel: optText(2000),
  /** Indeling: gevels, dakvlakken, verdiepingen. */
  layout: optText(2000),
  structuralCracks: optText(2000),
  lifespanSource: optText(500),
  /** Oud veld: aan = verval tot en met conditie 4. Gebruik `safetyNetOf`. */
  safetyNet: z.boolean().default(false),
  /** Vangnet (bijlage F): verval op leeftijd tot en met deze conditie; null = uit. */
  safetyNetMax: z.union([z.literal(2), z.literal(3), z.literal(4)]).nullish(),
  /** Leidend bij één gebrek: tabel 5 of bijlage B; leeg = tabel 5. */
  method: z.enum(SCORE_METHODS).optional(),
});
export type AssessmentInstruction = z.output<typeof assessmentInstructionSchema>;

/** Vangnet uit de instructie, ook voor metingen van vóór `safetyNetMax`. */
export function safetyNetOf(ins: { safetyNet?: boolean; safetyNetMax?: SafetyNetMax | null }): SafetyNetMax | null {
  return ins.safetyNetMax !== undefined ? ins.safetyNetMax : ins.safetyNet ? 4 : null;
}
export const safetyNetText = (max: SafetyNetMax | null) =>
  max == null ? "vangnet uit" : `vangnet: verval tot en met conditie ${max}`;

export const assessmentCreateSchema = z.object({
  id: z.uuid().optional(),
  assessedOn: z.iso.date().nullish(),
  assessorUserId: z.uuid().nullish(),
  defectCatalogId: z.uuid().nullish(),
  disciplines: z.array(z.enum(DISCIPLINES)).max(4).default([]),
  instruction: assessmentInstructionSchema.default({ excludedAssetIds: [], safetyNet: false }),
});
export type AssessmentCreateBody = z.input<typeof assessmentCreateSchema>;
export const assessmentUpdateSchema = z.object({
  assessedOn: z.iso.date().nullish(),
  assessorUserId: z.uuid().nullish(),
  defectCatalogId: z.uuid().nullish(),
  disciplines: z.array(z.enum(DISCIPLINES)).max(4).optional(),
  instruction: assessmentInstructionSchema.optional(),
});
export type AssessmentUpdateBody = z.input<typeof assessmentUpdateSchema>;

export const catalogCreateSchema = z.object({
  id: z.uuid().optional(),
  name: z.string().trim().min(1, "Geef de lijst een naam").max(120),
  version: z.string().trim().min(1).max(40).default("1"),
  codeScheme: z.enum(CODE_SCHEMES).default("nlbe-sfb-2025"),
});
export const catalogUpdateSchema = z.object({
  name: z.string().trim().min(1).max(120).optional(),
  active: z.boolean().optional(),
});
export const catalogVersionSchema = z.object({ version: z.string().trim().min(1).max(40) });

/** Eén regel in een eigen gebrekenlijst (handmatig of uit CSV). */
export const catalogItemSchema = z
  .object({
    partCode: optText(20),
    partName: optText(200),
    defectCode: optText(40),
    description: z.string().trim().min(1, "Omschrijving ontbreekt").max(500),
    defectType: z.enum(["specific", "component_ageing", "subcomponent_ageing", "optional"]),
    defectCategory: optText(120),
    severity: z.enum(DEFECT_SEVERITIES).nullish(),
    requiredIntensity: z.enum(DEFECT_INTENSITIES).nullish(),
    lifespanYears: z.number().int().min(1).max(200).nullish(),
    examplesComponents: optText(1000),
    examplesSubcomponents: optText(1000),
  })
  .superRefine((v, ctx) => {
    if (v.defectType === "specific" && !v.severity)
      ctx.addIssue({ code: "custom", path: ["severity"], message: "Een specifiek gebrek heeft een vaste ernst" });
    const fixed =
      v.defectType === "component_ageing" ? "serious" : v.defectType === "subcomponent_ageing" ? "minor" : null;
    if (fixed && v.severity && v.severity !== fixed)
      ctx.addIssue({
        code: "custom",
        path: ["severity"],
        message: `${DEFECT_TYPE_LABELS[v.defectType]} is altijd ${DEFECT_SEVERITY_LABELS[fixed].toLowerCase()}`,
      });
  });
export type CatalogItemInput = z.input<typeof catalogItemSchema>;
export const catalogImportSchema = z.object({
  csv: z.string().min(1).max(2_000_000),
  mode: z.enum(["append", "replace"]).default("append"),
  dryRun: z.boolean().default(true),
});

// ── CSV ──

export const CATALOG_CSV_COLUMNS = [
  "part_code",
  "part_name",
  "defect_code",
  "description",
  "defect_type",
  "defect_category",
  "severity",
  "required_intensity",
  "lifespan_years",
  "examples_components",
  "examples_subcomponents",
] as const;

/** CSV met `;` of `,` (uit de kopregel), aanhalingstekens volgens RFC 4180. */
export function parseCsv(text: string): string[][] {
  const src = text.replace(/^\uFEFF/, "");
  const firstLine = src.split(/\r?\n/, 1)[0] ?? "";
  const sep = (firstLine.match(/;/g)?.length ?? 0) >= (firstLine.match(/,/g)?.length ?? 0) ? ";" : ",";
  const rows: string[][] = [];
  let row: string[] = [];
  let cell = "";
  let quoted = false;
  for (let i = 0; i < src.length; i++) {
    const c = src[i]!;
    if (quoted) {
      if (c === '"' && src[i + 1] === '"') {
        cell += '"';
        i++;
      } else if (c === '"') quoted = false;
      else cell += c;
    } else if (c === '"') quoted = true;
    else if (c === sep) {
      row.push(cell);
      cell = "";
    } else if (c === "\n" || c === "\r") {
      if (c === "\r" && src[i + 1] === "\n") i++;
      row.push(cell);
      if (row.some((x) => x.trim())) rows.push(row);
      row = [];
      cell = "";
    } else cell += c;
  }
  row.push(cell);
  if (row.some((x) => x.trim())) rows.push(row);
  return rows;
}

/** Nederlandse waarden in de CSV naar de sleutels (gering → minor enz.). */
const ALIASES: Record<string, Record<string, string>> = {
  severity: { gering: "minor", serieus: "serious", ernstig: "critical" },
  required_intensity: {
    beginstadium: "initial",
    begin: "initial",
    gevorderd: "advanced",
    "gevorderd stadium": "advanced",
    eindstadium: "final",
    eind: "final",
  },
  defect_type: {
    specifiek: "specific",
    "veroudering component": "component_ageing",
    "veroudering onderdeel": "subcomponent_ageing",
    "veroudering onderdeel van component": "subcomponent_ageing",
    facultatief: "optional",
  },
};

export interface CatalogCsvResult {
  items: z.output<typeof catalogItemSchema>[];
  errors: { line: number; message: string }[];
}

/** CSV naar regels; fouten per regelnummer (kopregel = 1). Onbekende kolommen worden genegeerd. */
export function parseCatalogCsv(text: string): CatalogCsvResult {
  const rows = parseCsv(text);
  const errors: CatalogCsvResult["errors"] = [];
  const items: CatalogCsvResult["items"] = [];
  const head = (rows[0] ?? []).map((h) => h.trim().toLowerCase());
  if (!head.includes("description") || !head.includes("defect_type")) {
    errors.push({ line: 1, message: "Kopregel mist de kolommen description en defect_type" });
    return { items, errors };
  }
  rows.slice(1).forEach((r, k) => {
    const raw: Record<string, string> = {};
    head.forEach((h, i) => (raw[h] = (r[i] ?? "").trim()));
    const val = (col: string) => {
      const v = raw[col] ?? "";
      if (!v) return null;
      return ALIASES[col]?.[v.toLowerCase()] ?? v;
    };
    const life = val("lifespan_years");
    const parsed = catalogItemSchema.safeParse({
      partCode: val("part_code"),
      partName: val("part_name"),
      defectCode: val("defect_code"),
      description: raw.description ?? "",
      defectType: val("defect_type"),
      defectCategory: val("defect_category"),
      severity: val("severity"),
      requiredIntensity: val("required_intensity"),
      lifespanYears: life == null ? null : /^\d+$/.test(life) ? Number(life) : NaN,
      examplesComponents: val("examples_components"),
      examplesSubcomponents: val("examples_subcomponents"),
    });
    if (parsed.success) items.push(parsed.data);
    else
      errors.push({
        line: k + 2,
        message: parsed.error.issues.map((i) => `${String(i.path[0] ?? "")}: ${i.message}`).join("; "),
      });
  });
  return { items, errors };
}

// ── DTO's ──

export interface DefectCatalogDto {
  id: string;
  /** null = systeemlijst. */
  orgId: string | null;
  name: string;
  source: CatalogSource;
  version: string;
  codeScheme: CodeScheme;
  active: boolean;
  itemCount: number;
  /** Bouwdelen (alleen bij een JSON-import). */
  partCount: number;
  /** Gebruikt door een gestarte of afgeronde meting: regels liggen vast, maak een nieuwe versie. */
  locked: boolean;
}
export interface DefectCatalogItemDto {
  id: string;
  catalogId: string;
  partCode: string | null;
  partName: string | null;
  defectCode: string | null;
  description: string;
  defectType: DefectType;
  defectCategory: string | null;
  severity: DefectSeverity | null;
  requiredIntensity: DefectIntensity | null;
  lifespanYears: number | null;
  examplesComponents: string | null;
  examplesSubcomponents: string | null;
  optionalAspect: string | null;
}
/** Bouwdeel in een gebrekenlijst: naam, specificaties, levensduren en voorbeeldcomponenten. */
export interface DefectCatalogPartDto {
  code: string;
  name: string;
  discipline: Discipline | null;
  specs: { code: string; text: string }[];
  lifespans: { text: string; years: number }[];
  components: string[];
  /** NLBE-SfB-codes waarbij dit bouwdeel als voorstel verschijnt (koppeltabel). */
  elementCodes: string[];
  defectCount: number;
}

/**
 * Gebrekenlijst als JSON (§6.3). Zo komt een NEN 2767-2-lijst bij een organisatie met licentie; de
 * inhoud zit niet in de code. Ernst en intensiteit met de sleutels van de app.
 */
export const CATALOG_JSON_FORMAT = "klantpunt-gebrekenlijst";
export const catalogJsonSchema = z.object({
  format: z.literal(CATALOG_JSON_FORMAT),
  formatVersion: z.literal(1),
  name: z.string().trim().min(1).max(120),
  version: z.string().trim().min(1).max(40),
  source: z.enum(["nen2767-2", "own"]),
  codeScheme: z.enum(CODE_SCHEMES),
  parts: z
    .array(
      z.object({
        code: z.string().trim().min(1).max(20),
        name: z.string().trim().min(1).max(200),
        discipline: z.enum(DISCIPLINES).nullish(),
        specs: z
          .array(z.object({ code: z.string().max(20), text: z.string().max(500) }))
          .max(500)
          .default([]),
        lifespans: z
          .array(z.object({ text: z.string().max(500), years: z.number().int().min(1).max(200) }))
          .max(100)
          .default([]),
        components: z.array(z.string().trim().min(1).max(200)).max(200).default([]),
        elementCodes: z.array(z.string().trim().min(1).max(20)).max(50).default([]),
        defects: z
          .array(
            z.object({
              code: z.string().trim().max(40).nullish(),
              description: z.string().trim().min(1).max(500),
              category: z.string().trim().max(120).nullish(),
              optional: z.boolean().default(false),
              severity: z.enum(DEFECT_SEVERITIES).nullish(),
              requiredIntensity: z.enum(DEFECT_INTENSITIES).nullish(),
            }),
          )
          .max(2000),
      }),
    )
    .min(1)
    .max(500),
});
export type CatalogJson = z.input<typeof catalogJsonSchema>;
export const catalogJsonImportSchema = z.object({
  data: z.unknown(),
  dryRun: z.boolean().default(true),
});
export interface CatalogJsonImportResult {
  name: string;
  version: string;
  parts: number;
  defects: number;
  errors: string[];
  /** Id van de nieuwe lijst (niet bij een voorbeeld of bij fouten). */
  catalogId: string | null;
}

/** Bouwdelen die bij een NLBE-SfB-code passen (koppeltabel): langste prefix eerst. */
export function suggestParts<T extends { elementCodes: string[] }>(
  parts: readonly T[],
  elementCode: string | null,
): T[] {
  if (!elementCode) return [];
  const code = elementCode.trim();
  // NLBE-SfB: 53.21 hoort onder 53.2 en onder 53. Een asset met alleen "56" krijgt ook de lijsten van 56.x,
  // maar na die van een passende code.
  const weight = (c: string) => (code.startsWith(c) ? c.length : c.startsWith(code) ? code.length - 0.5 : 0);
  return parts
    .map((p) => ({ p, len: Math.max(0, ...p.elementCodes.map(weight)) }))
    .filter((x) => x.len > 0)
    .sort((a, b) => b.len - a.len)
    .map((x) => x.p);
}

export interface CatalogImportResult {
  items: z.output<typeof catalogItemSchema>[];
  errors: { line: number; message: string }[];
  /** Aantal geschreven regels (0 bij een voorbeeld of bij fouten). */
  written: number;
}
export interface AssessmentDto {
  id: string;
  buildingId: string;
  status: AssessmentStatus;
  assessedOn: string | null;
  assessor: { id: string; name: string | null } | null;
  defectCatalog: { id: string; name: string; version: string } | null;
  disciplines: Discipline[];
  instruction: AssessmentInstruction;
  scoreBuilding: number | null;
  completedAt: string | null;
  createdAt: string;
  updatedAt: string;
}
export interface AssessorOption {
  id: string;
  name: string | null;
  email: string;
}

export const isLibraryUnit = (u: string): u is LibraryUnit => (LIBRARY_UNITS as readonly string[]).includes(u);

// ── M8b: gebreken, score per asset, afronden ──

/**
 * Tot de rekenregels uit NEN 2767-1 er zijn (§7, §13) vult de inspecteur per asset een voorlopige
 * score in. Die wordt opgeslagen als correctie (override) met deze vaste reden, zodat hij in het
 * rapport zichtbaar is en later door de berekende score kan worden vervangen.
 */
export const PROVISIONAL_SCORE_REASON = "Voorlopige score: rekenregels NEN 2767-1 nog niet beschikbaar";
export const CONDITION_SCORES = [1, 2, 3, 4, 5, 6] as const;
export type ConditionScore = (typeof CONDITION_SCORES)[number];
/** Omschrijving per conditie (NEN 2767: 1 uitstekend … 6 zeer slecht). */
export const CONDITION_SCORE_LABELS: Record<ConditionScore, string> = {
  1: "Uitstekend",
  2: "Goed",
  3: "Redelijk",
  4: "Matig",
  5: "Slecht",
  6: "Zeer slecht",
};

export const defectCreateSchema = z.object({
  id: z.uuid(),
  assetId: z.uuid(),
  /** null = eigen gebrek zonder lijstregel. */
  catalogDefectId: z.uuid().nullish(),
  description: optText(1000),
  defectLocation: z.enum(DEFECT_LOCATIONS).default("asset"),
  componentNote: optText(200),
  severity: z.enum(DEFECT_SEVERITIES).nullish(),
  intensity: z.enum(DEFECT_INTENSITIES).nullish(),
  extentPercent: z.number().min(0).max(100).nullish(),
  optionalAspect: optText(120),
  /** Soort bij een eigen gebrek (bepaalt de ernst). */
  defectCategory: optText(120),
  /** Gestapeld op een ander gebrek van dezelfde asset (zelfde plek, §5.3). */
  stackedOnId: z.uuid().nullish(),
  floorId: z.uuid().nullish(),
  x: z.number().min(0).max(1).nullish(),
  y: z.number().min(0).max(1).nullish(),
});
export type DefectCreateBody = z.input<typeof defectCreateSchema>;
export const defectUpdateSchema = defectCreateSchema
  .omit({ id: true, assetId: true })
  .partial()
  // Zonder standaardwaarde: alleen meegestuurde velden wijzigen.
  .extend({ defectLocation: z.enum(DEFECT_LOCATIONS).optional() });

export const assetScoreUpdateSchema = z
  .object({
    notInspectable: z.boolean().optional(),
    /** Geïnspecteerd: ook zonder gebreken heeft de asset dan een oordeel (conditie 1). */
    inspected: z.boolean().optional(),
    remarks: optText(2000),
    /** Correctie op de berekende score, met reden; null wist hem. */
    overrideScore: z.number().int().min(1).max(6).nullish(),
    overrideReason: optText(500),
    quantity: z.number().min(0).max(1_000_000).nullish(),
    unit: z.enum(LIBRARY_UNITS).nullish(),
    /** Gebrekenlijst (bouwdeel) van de asset, gekozen op naam; wordt op de asset bewaard. */
    catalogPartCode: optText(20),
    catalogPartSpec: optText(20),
    /** Levensduur overnemen uit de lijst (op de asset). */
    lifespanYears: z.number().int().min(1).max(200).nullish(),
  })
  .refine((v) => v.overrideScore == null || (v.overrideReason != null && v.overrideReason !== ""), {
    path: ["overrideReason"],
    message: "Een correctie heeft een reden nodig",
  });
export type AssetScoreUpdateBody = z.input<typeof assetScoreUpdateSchema>;

export interface ConditionDefectDto {
  id: string;
  assetId: string;
  catalogDefectId: string | null;
  /** Omschrijving uit de lijst of de eigen toelichting. */
  title: string;
  description: string | null;
  defectLocation: DefectLocation;
  componentNote: string | null;
  severity: DefectSeverity;
  intensity: DefectIntensity | null;
  extentPercent: number | null;
  isOptional: boolean;
  optionalAspect: string | null;
  defectCategory: string | null;
  defectCode: string | null;
  stackedOnId: string | null;
  findingId: string | null;
  /** Foto's (file-ID's) bij dit gebrek. */
  photos: string[];
  createdAt: string;
}
export interface ConditionAssetDto {
  asset: {
    id: string;
    code: string;
    name: string;
    elementCode: string | null;
    assetKind: AssetKind;
    floorId: string | null;
    yearBuilt: number | null;
    lifespanYears: number | null;
    quantity: number | null;
    unit: LibraryUnit | null;
    catalogPartCode: string | null;
    catalogPartSpec: string | null;
    /** Vervangingswaarde (som van de vervangingsposten in het MJOP, centen); weging gebouwscore. */
    replacementValueCents: number | null;
    /** Onderdelen (docs/ASSETS-MJOP.md, Onderdelen): geen eigen score, wel te kiezen als component bij een gebrek. */
    parts: { id: string; code: string; name: string }[];
  };
  calculatedScore: number | null;
  /** Uitkomst bijlage B (correctiefactor bij het samenvoegen). */
  calculatedOutcome: number | null;
  /** Hoe de score is berekend. */
  how: AssetScoreHow | null;
  overrideScore: number | null;
  overrideReason: string | null;
  theoreticalScore: number | null;
  ageRatio: number | null;
  /** Voorstel verval (vangnet), null als het vangnet uit staat of de asset jong genoeg is. */
  decayIntensity: DefectIntensity | null;
  effectiveScore: number | null;
  notInspectable: boolean;
  inspected: boolean;
  remarks: string | null;
  defects: ConditionDefectDto[];
  /** Waarschuwingen bij controle (§10.3). */
  warnings: string[];
}
export interface AssessmentDetailDto {
  assessment: AssessmentDto;
  assets: ConditionAssetDto[];
  /** Regels uit de gebrekenlijst van de meting plus de generieke gebreken. */
  catalogItems: DefectCatalogItemDto[];
  /** Bouwdelen van de gebrekenlijst (kiezen op naam, componenten, levensduren). */
  catalogParts: DefectCatalogPartDto[];
  /** Assets zonder oordeel: afronden kan pas als dit 0 is. */
  open: number;
  building: BuildingScoreDto;
}
export interface BuildingScoreDto {
  score: number | null;
  outcome: number | null;
  /** Totale vervangingswaarde van de meegetelde assets (centen). */
  valueCents: number;
  /** Assets met een score maar zonder vervangingswaarde: tellen niet mee. */
  missingValue: number;
  disciplines: {
    discipline: Discipline;
    score: number | null;
    outcome: number | null;
    valueCents: number;
    assets: number;
  }[];
}
export interface AssetConditionDto {
  score: number | null;
  assessedOn: string | null;
  /** Theoretische conditie van vandaag (§7a), null zonder bouwjaar of levensduur. */
  theoreticalToday: number | null;
  ageRatioToday: number | null;
  /** "levensduur onbekend" / "bouwjaar onbekend". */
  missing: string[];
  history: {
    assessmentId: string;
    assessedOn: string | null;
    effectiveScore: number | null;
    theoreticalScore: number | null;
    provisional: boolean;
    notInspectable: boolean;
    defects: ConditionDefectDto[];
  }[];
}

/**
 * Waarschuwingen per asset bij controle (§10.3) en of hij een oordeel heeft: een (voorlopige) score of
 * "niet te inspecteren".
 */
export const NO_JUDGEMENT = "Nog geen oordeel";
export const NO_REPLACEMENT_VALUE = "Vervangingswaarde ontbreekt (telt niet mee in de gebouwscore)";
export function assetReview(a: {
  notInspectable: boolean;
  inspected: boolean;
  defectCount: number;
  overrideScore: number | null;
  yearBuilt: number | null;
  lifespanYears: number | null;
  hasReplacementValue: boolean;
  /** Vangnet stelt verval voor, maar er is nog geen verval vastgelegd. */
  decayMissing?: boolean;
}): { judged: boolean; warnings: string[] } {
  const judged = a.notInspectable || a.inspected || a.defectCount > 0 || a.overrideScore != null;
  const warnings: string[] = [];
  if (!judged) warnings.push(NO_JUDGEMENT);
  if (!a.hasReplacementValue && !a.notInspectable) warnings.push(NO_REPLACEMENT_VALUE);
  if (a.decayMissing) warnings.push("Vangnet: verval nog niet beoordeeld");
  if (!a.yearBuilt) warnings.push("Bouwjaar onbekend");
  if (!a.lifespanYears) warnings.push("Levensduur onbekend");
  return { judged, warnings };
}

// ── M8c: MJOP-voorstellen, gebrek → bevinding, foto's ──

/**
 * Voorstel voor het vervangingsjaar per score (§8). `later`: naar bouwjaar + levensduur, niet eerder
 * dan nu + 5 jaar; `within`: uiterlijk over `years` jaar; `none`: geen wijziging. Per organisatie aan
 * te passen; dit is het startpunt uit de spec.
 */
export const PROPOSAL_ACTIONS = ["later", "none", "within"] as const;
export type ProposalAction = (typeof PROPOSAL_ACTIONS)[number];
export const PROPOSAL_ACTION_LABELS: Record<ProposalAction, string> = {
  later: "Vervanging mag later",
  none: "Geen wijziging",
  within: "Vervanging binnen … jaar",
};
export const proposalRuleSchema = z.object({
  action: z.enum(PROPOSAL_ACTIONS),
  years: z.number().int().min(1).max(30).nullish(),
});
export type ProposalRule = z.infer<typeof proposalRuleSchema>;
export const proposalRulesSchema = z.object({
  "1": proposalRuleSchema,
  "2": proposalRuleSchema,
  "3": proposalRuleSchema,
  "4": proposalRuleSchema,
  "5": proposalRuleSchema,
  "6": proposalRuleSchema,
});
export type ProposalRules = z.infer<typeof proposalRulesSchema>;
export const DEFAULT_PROPOSAL_RULES: ProposalRules = {
  "1": { action: "later" },
  "2": { action: "later" },
  "3": { action: "none" },
  "4": { action: "within", years: 3 },
  "5": { action: "within", years: 1 },
  "6": { action: "within", years: 1 },
};
/** "Niet eerder dan nu + 5 jaar" bij een uitstelvoorstel (§8). */
export const PROPOSAL_MIN_LATER_YEARS = 5;

/** Eerstvolgende uitvoerjaar van een post vanaf dit jaar (of het eerste jaar als dat al voorbij is). */
export function nextOccurrence(
  firstYear: number,
  cycleYears: number | null,
  lastYear: number | null,
  thisYear: number,
) {
  if (!cycleYears || firstYear >= thisYear) return firstYear;
  const n = Math.ceil((thisYear - firstYear) / cycleYears);
  const y = firstYear + n * cycleYears;
  return lastYear != null && y > lastYear ? firstYear + (n - 1) * cycleYears : y;
}

/** Voorgesteld vervangingsjaar, of null als er niets verandert. */
export function proposeReplacementYear(p: {
  score: number;
  currentYear: number;
  thisYear: number;
  installedYear: number | null;
  lifespanYears: number | null;
  rules?: ProposalRules;
}): number | null {
  const rule = (p.rules ?? DEFAULT_PROPOSAL_RULES)[String(p.score) as keyof ProposalRules];
  if (!rule || rule.action === "none") return null;
  if (rule.action === "within") {
    const target = p.thisYear + (rule.years ?? 1);
    return p.currentYear > target ? target : null;
  }
  if (!p.installedYear || !p.lifespanYears) return null;
  const target = Math.max(p.installedYear + p.lifespanYears, p.thisYear + PROPOSAL_MIN_LATER_YEARS);
  return target > p.currentYear ? target : null;
}

export const PROPOSAL_STATUSES = ["open", "accepted", "rejected"] as const;
export type ProposalStatus = (typeof PROPOSAL_STATUSES)[number];
export const PROPOSAL_STATUS_LABELS: Record<ProposalStatus, string> = {
  open: "Open",
  accepted: "Overgenomen",
  rejected: "Afgewezen",
};
export interface ConditionProposalDto {
  id: string;
  assessmentId: string;
  asset: { id: string; code: string; name: string };
  budgetItemId: string;
  description: string;
  currentYear: number;
  proposedYear: number;
  score: number;
  theoreticalScore: number | null;
  reason: string;
  status: ProposalStatus;
  decidedAt: string | null;
}
export interface ConditionSettingsDto {
  proposalRules: ProposalRules;
}
export const conditionSettingsSchema = z.object({ proposalRules: proposalRulesSchema });

/** Gebrek → bevinding (§8); optioneel een raming die een herstelpost in het MJOP maakt. */
export const defectFindingSchema = z.object({
  estimatedCostCents: z.number().int().min(0).max(1_000_000_000).nullish(),
  plannedRepairYear: z.number().int().min(1900).max(2200).nullish(),
});
export type DefectFindingBody = z.input<typeof defectFindingSchema>;
export const defectPhotoSchema = z.object({ fileId: z.uuid() });

/** Antwoorden voor de bevinding uit de ernst van het gebrek (prioriteit volgt uit de vragenlijst). */
export function defectAnswers(severity: DefectSeverity): { danger: string; impact: string; worsen: string } {
  return severity === "critical"
    ? { danger: "mogelijk", impact: "deel", worsen: "snel" }
    : severity === "serious"
      ? { danger: "nee", impact: "hinder", worsen: "langzaam" }
      : { danger: "nee", impact: "geen", worsen: "nee" };
}

/** Normversies voor het rapport (§10.7). */
export const CONDITION_NORMS = [
  "NEN 2767-1+C1:2019 (methodiek)",
  "NEN 2767-2:2025 (gebrekenlijsten gebouwen)",
] as const;
export const CONDITION_REPORT_INTRO = "Een asset is in dit rapport een bouwdeel volgens NEN 2767.";

// ── Gebouwscore ──

/** Discipline van een asset uit de NL-SfB-code: 5 W, 6 E (66 transport T), de rest B. */
export function assetDiscipline(elementCode: string | null | undefined, assetKind?: AssetKind): Discipline {
  const c = elementCode?.trim() ?? "";
  if (c.startsWith("66")) return "T";
  if (c.startsWith("5")) return "W";
  if (c.startsWith("6")) return "E";
  if (!c && assetKind === "installation") return "W";
  return "B";
}

/**
 * Gebouwscore en score per discipline (§5.5, bijlage B): vervangingswaarde × correctiefactor.
 * Assets zonder vervangingswaarde tellen niet mee en worden geteld als waarschuwing.
 */
export function buildingScore(
  assets: readonly {
    discipline: Discipline;
    score: number | null;
    outcome: number | null;
    valueCents: number | null;
  }[],
): BuildingScoreDto {
  const scored = assets.filter((a) => a.score != null);
  const counted = scored.filter((a) => (a.valueCents ?? 0) > 0);
  const agg = (xs: typeof counted) =>
    aggregateScore(xs.map((a) => ({ value: a.valueCents!, score: a.score!, outcome: a.outcome })));
  const all = agg(counted);
  return {
    score: all?.score ?? null,
    outcome: all ? Math.round(all.outcome * 10000) / 10000 : null,
    valueCents: all?.value ?? 0,
    missingValue: scored.length - counted.length,
    disciplines: DISCIPLINES.flatMap((discipline) => {
      const xs = counted.filter((a) => a.discipline === discipline);
      if (!xs.length) return [];
      const r = agg(xs);
      return [
        {
          discipline,
          score: r?.score ?? null,
          outcome: r ? Math.round(r.outcome * 10000) / 10000 : null,
          valueCents: r?.value ?? 0,
          assets: xs.length,
        },
      ];
    }),
  };
}
